import { useCallback, useEffect, useState } from "react";

import breakpoints from "@/themes/base/breakpoints";
import { TabsOrientation } from "@/types/tab.types";

type UseTabsProps<T = string> = {
  initialTab: T;
  tabs: Array<{ value: T }>;
  isEditing?: boolean;
};

type UseTabsReturn<T> = {
  activeTab: T;
  changeTab: (tab: T) => void;
  handleStepSubmit: () => void;
  handleBack: () => void;
  isTabDisabled: (tabValue: T) => boolean;
  tabsOrientation: TabsOrientation;
};

const useTabs = <T>({ initialTab, tabs, isEditing = false }: UseTabsProps<T>): UseTabsReturn<T> => {
  const [activeTab, setActiveTab] = useState<T>(initialTab);
  const [tabsOrientation, setTabsOrientation] = useState<TabsOrientation>("horizontal");

  useEffect(() => {
    function handleTabsOrientation() {
      const isSmallSize = window.innerWidth < breakpoints.values.sm;
      return isSmallSize ? setTabsOrientation("vertical") : setTabsOrientation("horizontal");
    }

    window.addEventListener("resize", handleTabsOrientation);

    handleTabsOrientation();

    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const changeTab = useCallback((tab: T) => {
    setActiveTab(tab);
  }, []);

  function handleStepSubmit() {
    const currentTabIndex = tabs.findIndex((tab) => tab.value === activeTab);
    const nextTab = tabs[currentTabIndex + 1]?.value;
    if (nextTab) {
      changeTab(nextTab);
    }
  }

  function handleBack() {
    const currentTabIndex = tabs.findIndex((tab) => tab.value === activeTab);
    if (currentTabIndex > 0) {
      changeTab(tabs[currentTabIndex - 1].value);
    }
  }

  function isTabDisabled(tabValue: T) {
    if (isEditing) return false;
    const tabIndex = tabs.findIndex((tab) => tab.value === tabValue);
    return tabIndex !== tabs.findIndex((tab) => tab.value === activeTab);
  }

  return {
    tabsOrientation,
    activeTab,
    changeTab,
    handleStepSubmit,
    handleBack,
    isTabDisabled,
  };
};

export default useTabs;
